import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { TransactionCategory } from '@shipstation/financial-services-ui-components';
import type { BillingInfoData } from '@shipstation/financial-services-ui-components';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService } from 'ui-components/services/notification-service';

import ModalPortal from '../modal-portal';
import { ErrorBoundary } from '../error-boundary';
import FsuiContextProvider from '../../financial-services-ui/fsui-context-provider';
import { FinancialServicesUIProviderWrapper } from '../../financial-services-ui/financial-services-ui-provider-wrapper';
import Global from '../../store/global-selectors';

import { WwexRegistrationForm } from './wwex-registration-form';
import { openWwexAccount } from './wwex-registration.api';
import type { WwexFormData } from './types';
import { messages } from './messages';
import { NEW_PAYMENT_METHOD_VALUE } from './components/auctane-pay-account-select';
import style from './wwex-registration-modal.module.less';

type WwexRegistrationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data: WwexFormData, sellerFreightProviderId: number | null) => void;
};

export const WwexRegistrationModal = ({ isOpen, onClose, onSuccess }: WwexRegistrationModalProps) => {
  const intl = useIntl();
  const modalHeaderId = useMemo(() => GuidService.newGuid(), []);
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [paymentSubmitFunction, setPaymentSubmitFunction] = useState<(() => void) | null>(null);
  const waitingForPaymentCreationRef = useRef(false);
  const hasTriggeredRegistrationRef = useRef(false);
  const [fsuiBillingInfo, setFsuiBillingInfo] = useState<BillingInfoData | undefined>(undefined);

  // Initialize form data with empty defaults
  const [formData, setFormData] = useState<Partial<WwexFormData>>({
    // User Information (empty)
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phoneNumber: '',

    // Billing Address (empty)
    billingFullName: '',
    billingCompanyName: '',
    billingAddress: {
      line1: '',
      line2: '',
      line3: '',
      city: '',
      state: '',
      postalCode: '',
      countryCode: 'US',
    },

    // Shipping Address (empty)
    shippingAddress: {
      line1: '',
      line2: '',
      line3: '',
      city: '',
      state: '',
      postalCode: '',
      countryCode: 'US',
    },

    // Payment & Terms
    paymentMethodId: '',
    autoPayAccepted: true,
    termsAccepted: false,

    // Hidden defaults
    title: 'Billing Manager',
    contactType: 'Billing Manager',
  });

  // Reset form when modal closes
  useEffect(() => {
    if (isOpen) {
      hasTriggeredRegistrationRef.current = false;
      waitingForPaymentCreationRef.current = false;
    } else {
      setShowAllErrors(false);
      setIsSubmitting(false);
      waitingForPaymentCreationRef.current = false;
      setFsuiBillingInfo(undefined); // Clear cached billing info
    }
  }, [isOpen]);

  const handleFormChange = useCallback((data: Partial<WwexFormData>) => {
    setFormData(data);
  }, []);

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  const handlePaymentSubmitFunctionReady = useCallback((submitFn: (() => void) | null) => {
    setPaymentSubmitFunction(() => submitFn);
  }, []);

  const handleFsuiBillingInfoUpdate = useCallback((billingInfo?: BillingInfoData) => {
    setFsuiBillingInfo(billingInfo);
  }, []);

  // Core submission logic extracted to avoid closure issues
  const submitRegistration = useCallback(
    async (dataToSubmit: Partial<WwexFormData>) => {
      setIsSubmitting(true);

      // Shipping address always mirrors billing address
      const finalDataToSubmit = { ...dataToSubmit, shippingAddress: dataToSubmit.billingAddress };

      try {
        const response = await openWwexAccount(finalDataToSubmit as WwexFormData);

        if (response.isSuccess) {
          // Success!
          NotificationService.showNotification({
            content: intl.formatMessage(messages.successNotification),
            notificationType: 'success',
          });

          if (onSuccess) {
            onSuccess(dataToSubmit as WwexFormData, response.sellerFreightProviderId);
          }

          onClose();
        } else {
          // API returned errors
          const errorMessages = response.errors?.map((e) => e.message).filter(Boolean) || [];
          const errorMessage =
            errorMessages.length > 0 ? errorMessages.join(', ') : response.message || intl.formatMessage(messages.unexpectedErrorOccurred);

          NotificationService.showNotification({
            content: intl.formatMessage(messages.registrationFailedWithMessage, { errorMessage }),
            notificationType: 'error',
          });
        }
      } catch (error) {
        // Defensive error handling to prevent white screen
        const errorMessage = error instanceof Error ? error.message : intl.formatMessage(messages.unexpectedErrorOccurred);

        NotificationService.showNotification({
          content: intl.formatMessage(messages.registrationFailedWithSupport, { errorMessage }),
          notificationType: 'error',
        });
      } finally {
        setIsSubmitting(false);
        waitingForPaymentCreationRef.current = false;
      }
    },
    [intl, onClose, onSuccess],
  );

  const handlePaymentMethodCreated = useCallback(
    (paymentMethodId: string, updatedFormData: Partial<WwexFormData>) => {
      // Update form data immediately with the complete data from the form
      setFormData(updatedFormData);

      // If we were waiting for payment creation, submit immediately with fresh data
      if (waitingForPaymentCreationRef.current) {
        hasTriggeredRegistrationRef.current = true;
        waitingForPaymentCreationRef.current = false;
        submitRegistration(updatedFormData);
      }
    },
    [submitRegistration],
  );

  const handleSubmit = useCallback(async () => {
    // Show all validation errors on submit attempt
    setShowAllErrors(true);

    if (!isFormValid) {
      NotificationService.showNotification({
        content: intl.formatMessage(messages.errorNotification),
        notificationType: 'error',
      });
      return;
    }

    // Special case: If user is creating a new payment method, trigger FSUI submission first
    if (formData.paymentMethodId === NEW_PAYMENT_METHOD_VALUE && !waitingForPaymentCreationRef.current) {
      if (paymentSubmitFunction) {
        waitingForPaymentCreationRef.current = true;
        setIsSubmitting(true);
        try {
          paymentSubmitFunction();
        } catch {
          setIsSubmitting(false);
          waitingForPaymentCreationRef.current = false;
        }
        // The payment form will call handlePaymentMethodCreated when done,
        // which will automatically trigger the registration
        return;
      }
      NotificationService.showNotification({
        content: intl.formatMessage(messages.paymentFormNotReady),
        notificationType: 'error',
      });
      return;
    }

    // Submit with current form data
    await submitRegistration(formData);
  }, [isFormValid, formData, intl, paymentSubmitFunction, submitRegistration]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <ModalPortal onClose={handleCancel}>
      <FsuiContextProvider currencyCode={sellerCurrencyCode ?? 'USD'} transactionCategory={TransactionCategory.AddFunds}>
        <FinancialServicesUIProviderWrapper onBillingInfoUpdate={handleFsuiBillingInfoUpdate}>
          <ErrorBoundary errorMessageId='speedShipRegistrationModal' onError={handleCancel}>
            <Modal className={style.modal}>
              <ModalHeader closeModal={handleCancel} id={modalHeaderId}>
                {intl.formatMessage(messages.modalTitle)}
              </ModalHeader>

              <ModalBody className={style.modalBody}>
                <WwexRegistrationForm
                  formData={formData}
                  fsuiBillingInfo={fsuiBillingInfo}
                  isDisabled={isSubmitting}
                  showAllErrors={showAllErrors}
                  onChange={handleFormChange}
                  onValidationChange={handleValidationChange}
                  onPaymentSubmitFunctionReady={handlePaymentSubmitFunctionReady}
                  onPaymentMethodCreated={handlePaymentMethodCreated}
                />
              </ModalBody>

              <ModalFooter>
                <Button
                  ariaLabel={intl.formatMessage(messages.cancelButton)}
                  disabled={isSubmitting}
                  onClick={handleCancel}
                  theme={THEME_CONSTANTS.white}
                >
                  {intl.formatMessage(messages.cancelButton)}
                </Button>
                <Button
                  ariaLabel={intl.formatMessage(messages.submitButton)}
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                  theme={THEME_CONSTANTS.primary}
                >
                  {intl.formatMessage(messages.submitButton)}
                </Button>
              </ModalFooter>
            </Modal>
          </ErrorBoundary>
        </FinancialServicesUIProviderWrapper>
      </FsuiContextProvider>
    </ModalPortal>
  );
};

// Re-export type for backward compatibility
export type { WwexFormData };
